import { Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { keywordHubs } from "@/lib/keyword-hubs";

/**
 * Keyword-anchored internal links between the keyword landing pages.
 * Rendered as plain <a> markup during SSR (no client-only gating), so every
 * link is present in the initial HTML for crawlers.
 */
export function RelatedKeywords({
  currentPath,
  heading = "Related keywords",
  className = "",
}: {
  /** Route path of the page rendering this block — excluded from the list */
  currentPath: string;
  heading?: string;
  className?: string;
}) {
  const hubs = keywordHubs.filter((h) => h.to !== currentPath);
  if (hubs.length === 0) return null;

  return (
    <section
      aria-labelledby="related-keywords"
      className={`mx-auto max-w-5xl px-6 pb-16 ${className}`}
    >
      <h2 id="related-keywords" className="text-2xl md:text-3xl font-bold mb-3">
        {heading}
      </h2>
      <p className="text-muted-foreground mb-8 max-w-2xl">
        The other most-searched Raj Shamani topics, each covered in depth on its
        own page.
      </p>
      <ul className="grid sm:grid-cols-2 gap-3">
        {hubs.map((h) => (
          <li key={h.to}>
            <Link
              to={h.to}
              title={h.title}
              className="group flex items-start gap-3 rounded-lg border border-border bg-card px-4 py-3 hover:border-gold/50 transition"
            >
              <h.icon size={16} className="text-gold mt-1 shrink-0" />
              <span>
                <span className="font-semibold inline-flex items-center gap-1.5">
                  {h.title}
                  <ArrowUpRight
                    size={13}
                    className="text-gold group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition"
                  />
                </span>
                <span className="block text-sm text-muted-foreground leading-relaxed">
                  {h.desc}
                </span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
